import { ArrowRight, BookOpen, Target, Award } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useEffect } from 'react';

export default function TrainingSimPage() {
  // Structured data for the training simulation

  useEffect(() => {
    // Check if gtag is available
    if (typeof window.gtag !== 'undefined') {
        window.gtag('event', 'page_view', {
            page_title: 'Home',
            page_location: window.location.href,
            page_path: window.location.pathname
        });
    }
}, []);
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "LearningResource",
    "name": "EmploSkills Business Training Simulation",
    "description": "Practice and perfect your business skills in a risk-free environment before joining the competition.",
    "provider": {
      "@type": "Organization",
      "name": "EmploSkills"
    },
    "audience": {
      "@type": "EducationalAudience",
      "educationalRole": "student"
    },
    "learningResourceType": "Simulation",
    "educationalLevel": "Secondary education",
    "offers": {
      "@type": "Offer",
      "availability": "http://schema.org/OnlineOnly",
      "price": "0",
      "priceCurrency": "USD"
    }
  };

  return (
    <>
      <Helmet>
        <title>Business Training Simulation | EmploSkills</title>
        <meta 
          name="description" 
          content="Practice business skills in our risk-free training simulation. Perfect for students preparing for the EmploSkills Business Challenge." 
        />
        <meta 
          name="keywords" 
          content="business simulation, training simulation, business skills, practice environment, student training" 
        />
        
        {/* Open Graph */}
        <meta property="og:title" content="Business Training Simulation | EmploSkills" />
        <meta property="og:description" content="Practice business skills in our risk-free training simulation." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://homeschool.emploskills.com/training-simulation" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section 
          aria-label="Hero"
          className="bg-primary text-white py-20 px-4"
        >
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Business Training Simulation
            </h1>
            <p className="text-xl mb-8">
              Practice and perfect your business skills in a risk-free environment
              before joining the competition.
            </p>
          </div>
        </section>

        {/* Features Section */}
        <section 
          aria-label="Features"
          className="py-16 px-4"
        >
          <div className="max-w-6xl mx-auto">
            <h2 className="sr-only">Training Features</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <article className="bg-white p-8 rounded-xl shadow-sm">
                <BookOpen 
                  className="w-12 h-12 text-primary mb-4" 
                  aria-hidden="true"
                />
                <h3 className="text-xl font-bold mb-3">Learn at Your Pace</h3>
                <p className="text-gray-600">
                  Take your time to understand business concepts through hands-on practice.
                </p>
              </article>
              <article className="bg-white p-8 rounded-xl shadow-sm">
                <Target 
                  className="w-12 h-12 text-primary mb-4" 
                  aria-hidden="true"
                />
                <h3 className="text-xl font-bold mb-3">Risk-Free Practice</h3>
                <p className="text-gray-600">
                  Test different strategies and learn from mistakes without consequences.
                </p>
              </article>
              <article className="bg-white p-8 rounded-xl shadow-sm">
                <Award 
                  className="w-12 h-12 text-primary mb-4" 
                  aria-hidden="true"
                />
                <h3 className="text-xl font-bold mb-3">Build Confidence</h3>
                <p className="text-gray-600">
                  Gain experience and confidence before entering the competition.
                </p>
              </article>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section 
          aria-label="Call to Action"
          className="py-16 px-4 bg-white"
        >
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-6">Ready to Start Training?</h2>
            <p className="text-xl text-gray-600 mb-8">
              Access our training simulation and start building your business skills today.
            </p>
            <Link 
              to="/simulation/start"  // Changed from external link to internal route
              className="inline-flex items-center bg-primary text-white px-8 py-3 rounded-full font-semibold hover:bg-blue-700 transition"
              aria-label="Start training simulation"
            >
              Launch Training Simulation 
              <ArrowRight className="ml-2" aria-hidden="true" />
            </Link>
          </div>
        </section>
      </div>
    </>
  );
}